import { Suspense, useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import CssBaseline from '@mui/material/CssBaseline';
import LanguageRedirect from 'shared/ui/LanguageRedirect';
import MainLayout from 'app/layouts/MainLayout';
import MarketQuotesLayout from 'app/layouts/MarketQuotesLayout';
import PublicLayout from 'app/layouts/PublicLayout';
import AuthRequired from 'features/auth/pages/login/AuthRequired';
import { UserProvider } from 'shared/contexts/UserContext';
import { CurrencyConversionProvider } from 'shared/contexts/CurrencyConversionContext';
import AppTheme from 'shared/theme/AppTheme';
import LoadingState from 'shared/ui/LoadingState';
import { UX_LOADING } from 'lib/uxTokens';
import { closeTopmostDialogOnBack } from 'utils/dialogBackButton';
import {
    LazyHomePage,
    LazyGroupDetails,
    LazyGroupEntrance,
    LazyInviteLanding,
    LazyBudgetMenu,
    LazyPersonalCalendarPage,
    LazyInvestmentsMenu,
    LazyCotizacionesMenu,
    LazyWalletPage,
    LazyError404,
    LazyProfilePage,
    LazySignIn,
    LazyRegister,
    LazyVerifyEmail,
    LazyResetPassword,
    LazyAdsLandingPage,
} from 'app/routeModules';

function RouteFallback() {
    return <LoadingState spinnerSize={UX_LOADING.routeSpinnerSize} minHeight={UX_LOADING.sectionMinHeight} />;
}

function lazyElement(element: React.ReactNode) {
    return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

function App() {
    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return;

        let handle: { remove: () => Promise<void> } | undefined;
        void CapacitorApp.addListener('backButton', ({ canGoBack }) => {
            if (closeTopmostDialogOnBack(document)) return;
            if (canGoBack) {
                window.history.back();
            } else {
                void CapacitorApp.exitApp();
            }
        }).then((h) => {
            handle = h;
        });

        return () => {
            void handle?.remove();
        };
    }, []);

    return (
        <AppTheme>
            <CssBaseline enableColorScheme />
            <UserProvider>
                <CurrencyConversionProvider>
                <LanguageRedirect>
                    <Routes>
                        {/* Language-specific routes */}
                        {/*public routes*/}
                        <Route path="/:language" element={<PublicLayout />}>
                            <Route index          element={lazyElement(<LazySignIn />)} />
                            <Route path="login"   element={lazyElement(<LazySignIn />)} />
                            <Route path="verify-email" element={lazyElement(<LazyVerifyEmail />)} />
                            <Route path="reset-password" element={lazyElement(<LazyResetPassword />)} />
                            <Route path="landing" element={lazyElement(<LazyAdsLandingPage />)} />
                            <Route path="*"       element={lazyElement(<LazyError404 />)} />
                        </Route>

                        {/*private routes*/}
                        <Route path="/:language" element={<MainLayout />} >
                            <Route path="invite/:token" element={lazyElement(<LazyInviteLanding />)} />
                            <Route path="register"      element={lazyElement(<LazyRegister />)} />
                            <Route path=':username'     element={<AuthRequired />} >
                                <Route index                                    element={lazyElement(<LazyProfilePage />)} />
                                <Route path="profile"                           element={lazyElement(<LazyProfilePage />)} />
                                <Route path="groups"                            element={lazyElement(<LazyHomePage />)} />
                                <Route path="groups/:groupName"                 element={lazyElement(<LazyGroupDetails />)} />
                                <Route path="groups/:groupName/invite/:token"   element={lazyElement(<LazyGroupEntrance />)} />
                                <Route path="personal"                          element={lazyElement(<LazyBudgetMenu />)} />
                                <Route path="personal/calendar"                 element={lazyElement(<LazyPersonalCalendarPage />)} />
                                <Route element={<MarketQuotesLayout />}>
                                    <Route path="cotizaciones"                  element={lazyElement(<LazyCotizacionesMenu />)} />
                                    <Route path="investments"                   element={lazyElement(<LazyInvestmentsMenu />)} />
                                </Route>
                                <Route path="wallet"                            element={lazyElement(<LazyWalletPage />)} />
                                <Route path="accounts"                          element={lazyElement(<LazyWalletPage />)} />
                                <Route path="*"                                element={lazyElement(<LazyError404 />)} />
                            </Route>
                        </Route>
                    </Routes>
                </LanguageRedirect>
                </CurrencyConversionProvider>
            </UserProvider>
        </AppTheme>
    );
}

export default App;
